// src/user/userApi.js
import { getAuthHeaders } from '../../utils/headers';

// Helper to safely parse JSON from fetch
const parseJsonSafely = async (response) => {
  const text = await response.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return {};
  }
};

export const fetchCustomers = async (authUser) => {
  try {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const apiKey = process.env.REACT_APP_API_KEY;
    const url = `${baseUrl}/api/v1/customers`;

    const response = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders(apiKey, authUser)
    });

    const data = await response.json();
    if (data?.isSuccess && Array.isArray(data.details)) {
      return data.details.map(customer => ({
        id: customer.customerId,
        customer: customer.customerName,
      }));
    } else {
      throw new Error("Invalid customer response format");
    }
  } catch (error) {
    console.error("Error fetching customers:", error);
    return [];
  }
};

export const fetchUsers = async (selectedCustomer, authUser) => {
  try {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const apiKey = process.env.REACT_APP_API_KEY;

    const url = selectedCustomer
      ? `${baseUrl}/api/v1/customers/${selectedCustomer}/users`
      : `${baseUrl}/api/v1/users`;

    const response = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders(apiKey, authUser)
    });

    const data = await response.json();
    if (Array.isArray(data.details)) {
      return data.details.map(user => ({
        id: user.user_id || user.id || user.userId,
        firstName: user.first_name || user.firstName || '',
        lastName: user.last_name || user.lastName || '',
        email: user.email || '',
        roles: Array.isArray(user.roles)
          ? user.roles
          : typeof user.roles === 'string'
            ? [user.roles]
            : user.roleTitles || [],
      }));
    } else {
      console.warn("Unexpected users format:", data);
      return [];
    }
  } catch (error) {
    console.error("Error fetching users:", error);
    return [];
  }
};

export const fetchRolesFromUsers = async (customerId, authUser) => {
  try {
    if (!customerId) return [];

    const baseUrl = process.env.REACT_APP_BASE_URL;
    const apiKey = process.env.REACT_APP_API_KEY;
    const url = `${baseUrl}/api/v1/customers/${customerId}/roles`;

    const response = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders(apiKey, authUser)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();

    if (!Array.isArray(data.details)) return [];

    const roleCounts = data.details.reduce((acc, item) => {
      const roleName = item.role?.trim();
      if (roleName) {
        acc[roleName] = (acc[roleName] || 0) + 1;
      }
      return acc;
    }, {});

    return Object.entries(roleCounts).map(([label, count]) => ({ label, count }));
  } catch (error) {
    console.error("Failed to fetch roles from users:", error);
    return [];
  }
};

export const fetchUserByEmail = async (email, authUser = null) => {
  try {
    const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:7200';
    const apiKey = process.env.REACT_APP_API_KEY;
    const url = `${baseUrl}/api/v1/users?email=${email}`;

    // For login scenarios where authUser might not be available yet
    const headers = authUser ? getAuthHeaders(apiKey, authUser) : {
      'Content-Type': 'application/json'
    };

    const response = await fetch(url, {
      method: 'GET',
      headers: headers
    });

    const data = await response.json();

    if (response.ok && data?.isSuccess) {
      return data;
    } else {
      console.error("User not found or invalid response:", data);
      return "";
    }
  } catch (error) {
    console.error("Error fetching users by email:", error);
    return "";
  }
};

export const fetchCustomerIdByTenantId = async (tenantId, authUser = null) => {
  try {
    const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:7200';
    const apiKey = process.env.REACT_APP_API_KEY;
    const url = `${baseUrl}/api/v1/customers`;

    console.log('fetchCustomerIdByTenantId - Request details:', {
      url,
      tenantId,
      hasAuthUser: !!authUser,
      hasApiKey: !!apiKey,
      authUserToken: authUser?.token ? 'Present' : 'Missing'
    });

    // For login scenarios where authUser might not be available yet
    const headers = authUser ? getAuthHeaders(apiKey, authUser) : {
      'Content-Type': 'application/json'
    };

    console.log('fetchCustomerIdByTenantId - Headers:', headers);

    const response = await fetch(url, {
      method: 'GET',
      headers: headers
    });

    console.log('fetchCustomerIdByTenantId - Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('fetchCustomerIdByTenantId - API Error:', {
        status: response.status,
        statusText: response.statusText,
        body: errorText
      });
      
      if (response.status === 401) {
        console.error('Authentication failed - check API key or token');
      }
      
      return null;
    }

    const data = await response.json();

    if (data?.isSuccess && Array.isArray(data.details) && data.details.length > 0) {
      console.log('fetchCustomerIdByTenantId - Success:', data.details[0].customerId);
      return data.details[0].customerId;
    } else {
      console.warn("No customers found for tenant ID:", tenantId, data);
      return null;
    }
  } catch (error) {
    console.error("Error fetching customer by tenant ID:", error);
    return null;
  }
};

export const fetchCustomerById = async (customerId, authUser = null) => {
  try {
    const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:7200';
    const apiKey = process.env.REACT_APP_API_KEY;
    const url = `${baseUrl}/api/v1/customers/${customerId}`;

    // For login scenarios where authUser might not be available yet
    const headers = authUser ? getAuthHeaders(apiKey, authUser) : {
      'Content-Type': 'application/json'
    };

    const response = await fetch(url, {
      method: 'GET',
      headers: headers
    });

    const data = await response.json();

    if (response.ok && data?.isSuccess) {
      return data;
    } else {
      console.error("Customer not found or invalid response:", data);
      return null;
    }
  } catch (error) {
    console.error("Error fetching customer by ID:", error);
    return null;
  }
};

export const createUser = async (customerId, userPayload, authUser) => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const apiKey = process.env.REACT_APP_API_KEY;
  const url = `${baseUrl}/api/v1/customers/${customerId}/users`;

  const response = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(apiKey, authUser),
    body: JSON.stringify(userPayload),
  });

  const data = await parseJsonSafely(response);

  if (!response.ok) {
    const errorObj = new Error(data?.message || data?.error || `API Error (${response.status})`);
    errorObj.fieldErrors = data?.fieldErrors || {};
    throw errorObj;
  }

  return data.user;
};

export const updateUser = async (customerId, userId, userPayload, authUser) => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const apiKey = process.env.REACT_APP_API_KEY;
  const url = `${baseUrl}/api/v1/customers/${customerId}/users/${userId}`;

  const response = await fetch(url, {
    method: 'PATCH',
    headers: getAuthHeaders(apiKey, authUser),
    body: JSON.stringify(userPayload),
  });

  const data = await parseJsonSafely(response);

  if (!response.ok) {
    const errorObj = new Error(data?.message || data?.error || `API Error (${response.status})`);
    errorObj.fieldErrors = data?.fieldErrors || {};
    throw errorObj;
  }

  return data.user;
};

export const deleteUser = async (customerId, userId, authUser) => {
  try {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const apiKey = process.env.REACT_APP_API_KEY;
    const url = `${baseUrl}/api/v1/customers/${customerId}/users/${userId}`;

    const response = await fetch(url, {
      method: 'DELETE',
      headers: getAuthHeaders(apiKey, authUser)
    });

    const responseText = await response.text();
    const data = responseText ? JSON.parse(responseText) : {};

    if (!response.ok) {
      throw new Error(data?.error || `Failed to delete user (Status ${response.status})`);
    }

    return { success: true };
  } catch (error) {
    throw new Error(error.message || "Failed to delete user");
  }
};
